import { _keys, debounce, filterDuplicates, Module, removeItemFromArray, Second } from '@nu-art/ts-common';
import { apiWithBody, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_FirebaseListener } from '@nu-art/firebase-frontend/ModuleFE_FirebaseListener/ModuleFE_FirebaseListener';
import { ApiDef_FocusedObject, } from '@nu-art/ts-focused-object-shared';
import { LoggedStatus, ModuleFE_Account } from '@nu-art/user-account-frontend/index';
import { DefaultTTL_FocusedObject, getRelationalPath } from '@nu-art/ts-focused-object-shared/consts';
export const dispatch_onFocusedDataReceived = new ThunderDispatcher('__onFocusedDataReceived');
export class ModuleFE_FocusedObject_Class extends Module {
    _v1;
    focusFirebaseListener;
    focusDataMap;
    currentlyFocused = {};
    apiDebounce;
    windowIsFocused = true;
    unfocusTimeout;
    keepAliveTimeout;
    __onLoginStatusUpdated() {
        const status = ModuleFE_Account.getLoggedStatus();
        if (status === LoggedStatus.LOGGED_OUT)
            this.onUserLoggedOut();
    }
    constructor() {
        super();
        this.focusDataMap = {};
        this._v1 = {
            // updateFocusData: apiWithBody(ApiDef_FocusedObject._v1.updateFocusData),
            // setFocusStatusByTabId: apiWithBody(ApiDef_FocusedObject._v1.setFocusStatusByTabId),
            // releaseObject: apiWithBody(ApiDef_FocusedObject._v1.releaseObject),
            // releaseByTabId: apiWithBody(ApiDef_FocusedObject._v1.releaseByTabId),
            update: apiWithBody(ApiDef_FocusedObject._v1.update),
        };
        this.apiDebounce = debounce(this.updateRTDB, 2 * Second, 10 * Second);
    }
    init() {
        this.initFirebaseListening();
        this.initWindowFocusListeners();
        this.initWindowCloseListeners();
    }
    // ######################## Init listeners ########################
    initFirebaseListening = () => {
        this.focusFirebaseListener = ModuleFE_FirebaseListener.createListener(getRelationalPath());
        this.focusFirebaseListener.startListening(this.onRTDBChange);
    };
    initWindowFocusListeners() {
        window.addEventListener('focus', this.onWindowFocus);
        window.addEventListener('blur', this.onWindowBlur);
    }
    initWindowCloseListeners() {
        window.addEventListener('beforeunload', async (event) => {
            this._v1.update({ focusedEntities: [] }).execute();
            // navigator.sendBeacon('/log', JSON.stringify({ type:'application/json' }));
        });
    }
    // ######################## Listener Callbacks ########################
    onRTDBChange = (snapshot) => {
        this.focusDataMap = snapshot.val() ?? {};
        this.logDebug('Received firebase focus data', this.focusDataMap);
        // Update all the FocusedEntityRef components
        dispatch_onFocusedDataReceived.dispatchAll(this.focusDataMap);
    };
    /**
     * Callback for when the current window is focused.
     * we change the class property "windowIsFocused" to true so when the time comes to
     * send a keepalive to BE, the request will be sent if the window is focused.
     * Will also trigger keepalive timer if the time is not already set
     */
    onWindowFocus = () => {
        this.windowIsFocused = true;
        //If the keep alive counter still exists, no need to trigger any extra further logic
        if (this.keepAliveTimeout)
            return;
        this.apiDebounce();
    };
    /**
     * Callback for when the current window is un-focused.
     * we change the class property "windowIsFocused" to false so when the time comes to
     * send a keepalive to BE, the request will not be sent if the window is not focused
     */
    onWindowBlur = () => {
        this.windowIsFocused = false;
    };
    onUserLoggedOut = () => {
        this.currentlyFocused = {};
    };
    // ######################## Timer Interactions ########################
    triggerKeepAlive = () => {
        this.clearKeepAlive();
        //No need to set keepalive timeout if currentlyFocused has no data
        if (!_keys(this.currentlyFocused).length)
            return;
        this.keepAliveTimeout = setTimeout(() => {
            //No need to keepalive if window is not focused
            if (!this.windowIsFocused)
                return this.clearKeepAlive();
            this.apiDebounce();
        }, DefaultTTL_FocusedObject - 20 * Second);
    };
    triggerUnfocus = () => {
        this.clearUnfocus();
        this.unfocusTimeout = setTimeout(() => this.apiDebounce(), 20 * Second);
    };
    clearKeepAlive = () => {
        clearTimeout(this.keepAliveTimeout);
        delete this.keepAliveTimeout;
    };
    clearUnfocus = () => {
        clearTimeout(this.unfocusTimeout);
        delete this.unfocusTimeout;
    };
    // ######################## API Logic ########################
    updateRTDB = () => {
        //Call API
        const focusedEntities = this.translateCurrentlyFocusedToFocusedEntities();
        this._v1.update({ focusedEntities })
            .executeSync()
            .then()
            .catch(e => {
            this.logError('Update focused object failed', e);
        })
            .finally(() => {
            this.clearUnfocus();
            this.triggerKeepAlive();
        });
    };
    // ######################## Logic ########################
    focus = (entities) => {
        entities.forEach(entity => {
            if (!this.currentlyFocused[entity.dbKey])
                this.currentlyFocused[entity.dbKey] = [];
            this.currentlyFocused[entity.dbKey] = filterDuplicates([...this.currentlyFocused[entity.dbKey], entity.itemId]);
        });
        this.apiDebounce();
    };
    unfocus = (entities) => {
        entities.forEach(entity => {
            if (!this.currentlyFocused[entity.dbKey])
                return;
            this.currentlyFocused[entity.dbKey] = removeItemFromArray(this.currentlyFocused[entity.dbKey], entity.itemId);
        });
        this.triggerUnfocus();
    };
    getFocusData = (dbKey, itemId) => {
        return this.focusDataMap[dbKey]?.[itemId];
    };
    getAccountIdsForFocusedItem = (dbKey, itemId, ignoreCurrentUser = true) => {
        const data = this.getFocusData(dbKey, itemId);
        const userIds = data ? _keys(data) : [];
        const account = ModuleFE_Account.getCurrentlyLoggedAccount();
        return ignoreCurrentUser ? userIds.filter(id => id !== account?._id) : userIds;
    };
    translateCurrentlyFocusedToFocusedEntities = () => {
        const focusedEntities = [];
        _keys(this.currentlyFocused).forEach(dbKey => {
            this.currentlyFocused[dbKey].forEach(itemId => {
                focusedEntities.push({ dbKey: dbKey, itemId });
            });
        });
        return focusedEntities;
    };
}
export const ModuleFE_FocusedObject = new ModuleFE_FocusedObject_Class();
//# sourceMappingURL=ModuleFE_FocusedObject.js.map